import React, { useState, useEffect } from "react";
import { 
  Megaphone, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  ShieldAlert, 
  Sparkles, 
  Hammer, 
  AlertTriangle, 
  Info, 
  Bell, 
  CheckCircle2, 
  X, 
  ExternalLink,
  Power,
  Calendar,
  Clock,
  Eye
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export interface MaintenanceBannerData {
  id: string;
  title: string;
  message: string;
  banner_type: 'maintenance' | 'warning' | 'info' | 'announcement';
  is_active: boolean;
  is_dismissible: boolean;
  action_text?: string | null;
  action_url?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  created_at?: string;
  updated_at?: string;
}

const DEFAULT_BANNER: Partial<MaintenanceBannerData> = {
  title: "Aviso de Mantenimiento",
  message: "",
  banner_type: "maintenance",
  is_active: true,
  is_dismissible: true,
  action_text: "",
  action_url: "",
  start_time: "",
  end_time: "",
};

export default function AdminBanners() {
  const { profile } = useAuth();
  const isAdmin = profile?.role === "admin";

  const [banners, setBanners] = useState<MaintenanceBannerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentBanner, setCurrentBanner] = useState<Partial<MaintenanceBannerData>>(DEFAULT_BANNER);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MaintenanceBannerData | null>(null);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("maintenance_banners")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setBanners(data || []);
    } catch (err: any) {
      console.error("Error al cargar banners de mantenimiento:", err);
      toast.error("Error al cargar banners de mantenimiento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchBanners();
    }
  }, [isAdmin]);

  const handleOpenCreateModal = () => {
    setCurrentBanner({ ...DEFAULT_BANNER });
    setIsDialogOpen(true);
  };

  const handleOpenEditModal = (banner: MaintenanceBannerData) => {
    setCurrentBanner({ ...banner });
    setIsDialogOpen(true);
  };

  const handleToggleActive = async (banner: MaintenanceBannerData) => {
    const nextState = !banner.is_active;
    try {
      const { error } = await supabase
        .from("maintenance_banners")
        .update({ is_active: nextState, updated_at: new Date().toISOString() })
        .eq("id", banner.id);

      if (error) throw error;

      setBanners((prev) =>
        prev.map((b) => (b.id === banner.id ? { ...b, is_active: nextState } : b))
      );
      toast.success(nextState ? "Banner activado en el sitio 🟢" : "Banner desactivado ⚪");
    } catch (err: any) {
      toast.error("Error al cambiar estado del banner");
    }
  };

  const handleSaveBanner = async () => {
    if (!currentBanner.message?.trim()) {
      toast.error("El mensaje del banner es obligatorio");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: currentBanner.title?.trim() || "Aviso de Mantenimiento",
        message: currentBanner.message.trim(),
        banner_type: currentBanner.banner_type || "maintenance",
        is_active: currentBanner.is_active ?? true,
        is_dismissible: currentBanner.is_dismissible ?? true,
        action_text: currentBanner.action_text?.trim() || null,
        action_url: currentBanner.action_url?.trim() || null,
        start_time: currentBanner.start_time || null,
        end_time: currentBanner.end_time || null,
        updated_at: new Date().toISOString(),
      };

      if (currentBanner.id) {
        const { error } = await supabase
          .from("maintenance_banners")
          .update(payload)
          .eq("id", currentBanner.id);

        if (error) throw error;
        toast.success("Banner actualizado correctamente");
      } else {
        const { error } = await supabase
          .from("maintenance_banners")
          .insert([{ ...payload, created_at: new Date().toISOString() }]);

        if (error) throw error;
        toast.success("Banner de mantenimiento creado correctamente");
      }

      setIsDialogOpen(false);
      fetchBanners();
    } catch (err: any) {
      console.error("Error al guardar banner:", err);
      toast.error("Error al guardar el banner: " + (err.message || ""));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBanner = async () => {
    if (!deleteTarget) return;
    try {
      const { error } = await supabase
        .from("maintenance_banners")
        .delete()
        .eq("id", deleteTarget.id);

      if (error) throw error;

      setBanners((prev) => prev.filter((b) => b.id !== deleteTarget.id));
      toast.success("Banner eliminado correctamente");
      setDeleteTarget(null);
    } catch (err: any) {
      toast.error("Error al eliminar el banner");
    }
  };

  // Filtered List
  const filteredBanners = banners.filter((b) => {
    const matchesSearch =
      b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.message.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === "all" || b.banner_type === typeFilter;
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && b.is_active) ||
      (statusFilter === "inactive" && !b.is_active);
    return matchesSearch && matchesType && matchesStatus;
  });

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 px-4 text-center">
        <div className="w-16 h-16 bg-rose-100 rounded-3xl flex items-center justify-center text-rose-600 mb-4 shadow-sm">
          <ShieldAlert className="h-8 w-8" />
        </div>
        <h2 className="text-xl font-bold text-slate-800">Acceso Restringido</h2>
        <p className="text-slate-500 text-sm max-w-md mt-1">
          Solo los administradores autorizados tienen permisos para gestionar los banners de mantenimiento del sitio.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-600 border border-amber-500/20">
              <Megaphone className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900">
                Gestor de Banners y Avisos de Mantenimiento
              </h1>
              <p className="text-xs font-semibold text-slate-500">
                Administra los anuncios, mantenimientos y alertas globales visibles para los usuarios del sitio.
              </p>
            </div>
          </div>
        </div>

        <Button
          onClick={handleOpenCreateModal}
          className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-md font-bold text-xs uppercase tracking-wider h-10 px-5 gap-2 transition-all hover:scale-[1.02] active:scale-95"
        >
          <Plus className="h-4 w-4" />
          Nuevo Banner
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200/80 shadow-xs bg-white">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-extrabold uppercase text-slate-400 tracking-wider">Total Banners</p>
              <h3 className="text-2xl font-black text-slate-800 mt-1">{banners.length}</h3>
            </div>
            <div className="p-3 bg-slate-100 rounded-2xl text-slate-600">
              <Megaphone className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200/80 shadow-xs bg-emerald-50/40">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-extrabold uppercase text-emerald-700 tracking-wider">Banners Activos</p>
              <h3 className="text-2xl font-black text-emerald-900 mt-1">
                {banners.filter((b) => b.is_active).length}
              </h3>
            </div>
            <div className="p-3 bg-emerald-100 rounded-2xl text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200/80 shadow-xs bg-amber-50/40">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-extrabold uppercase text-amber-700 tracking-wider">Mantenimiento</p>
              <h3 className="text-2xl font-black text-amber-900 mt-1">
                {banners.filter((b) => b.banner_type === "maintenance" && b.is_active).length}
              </h3>
            </div>
            <div className="p-3 bg-amber-100 rounded-2xl text-amber-700">
              <Hammer className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200/80 shadow-xs bg-blue-50/40">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-[11px] font-extrabold uppercase text-blue-700 tracking-wider">Anuncios / Avisos</p>
              <h3 className="text-2xl font-black text-blue-900 mt-1">
                {banners.filter((b) => b.banner_type !== "maintenance" && b.is_active).length}
              </h3>
            </div>
            <div className="p-3 bg-blue-100 rounded-2xl text-blue-700">
              <Bell className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Search Bar */}
      <Card className="border-slate-200 shadow-xs bg-white">
        <CardContent className="p-4 flex flex-col md:flex-row gap-3 justify-between items-center">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por título o contenido..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-xs rounded-xl bg-slate-50 border-slate-200"
            />
          </div>

          <div className="flex items-center gap-2.5 w-full md:w-auto overflow-x-auto pb-1 md:pb-0">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-9 text-xs rounded-xl w-[160px] bg-slate-50">
                <SelectValue placeholder="Tipo de Banner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los Tipos</SelectItem>
                <SelectItem value="maintenance">🛠️ Mantenimiento</SelectItem>
                <SelectItem value="warning">⚠️ Alerta / Advertencia</SelectItem>
                <SelectItem value="info">ℹ️ Información</SelectItem>
                <SelectItem value="announcement">📢 Anuncio / Noticia</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 text-xs rounded-xl w-[150px] bg-slate-50">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los Estados</SelectItem>
                <SelectItem value="active">🟢 Activos</SelectItem>
                <SelectItem value="inactive">⚪ Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Banners Table */}
      <Card className="border-slate-200 shadow-sm overflow-hidden bg-white">
        <CardHeader className="bg-slate-50/70 py-4 px-6 border-b border-slate-100">
          <CardTitle className="text-sm font-bold text-slate-800 uppercase tracking-wider">
            Listado de Banners Configurados ({filteredBanners.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-slate-400 text-xs font-semibold">
              Cargando banners de mantenimiento...
            </div>
          ) : filteredBanners.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-xs font-semibold">
              No se encontraron banners con los criterios seleccionados.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/40 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                  <TableHead className="w-16">Estado</TableHead>
                  <TableHead className="w-[140px]">Tipo</TableHead>
                  <TableHead>Título y Mensaje</TableHead>
                  <TableHead className="w-[180px]">Botón / Acción</TableHead>
                  <TableHead className="w-[120px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBanners.map((banner) => (
                  <TableRow key={banner.id} className="hover:bg-slate-50/60 transition-colors">
                    <TableCell>
                      <Switch
                        checked={banner.is_active}
                        onCheckedChange={() => handleToggleActive(banner)}
                      />
                    </TableCell>
                    <TableCell>
                      {banner.banner_type === "maintenance" && (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] font-bold">
                          🛠️ Mantenimiento
                        </Badge>
                      )}
                      {banner.banner_type === "warning" && (
                        <Badge variant="secondary" className="bg-rose-100 text-rose-800 border-rose-200 text-[10px] font-bold">
                          ⚠️ Alerta
                        </Badge>
                      )}
                      {banner.banner_type === "info" && (
                        <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200 text-[10px] font-bold">
                          ℹ️ Información
                        </Badge>
                      )}
                      {banner.banner_type === "announcement" && (
                        <Badge variant="secondary" className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] font-bold">
                          📢 Anuncio
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-xs text-slate-800">{banner.title}</span>
                        <span className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                          {banner.message}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {banner.action_text ? (
                        <div className="flex items-center gap-1.5 text-xs text-blue-600 font-semibold truncate">
                          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{banner.action_text}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 font-normal">Sin acción</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEditModal(banner)}
                          className="h-8 w-8 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Editar banner"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(banner)}
                          className="h-8 w-8 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg"
                          title="Eliminar banner"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* CREATE / EDIT BANNER DIALOG */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-amber-500" />
              {currentBanner.id ? "Editar Banner de Mantenimiento" : "Crear Nuevo Banner de Mantenimiento"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Configura los detalles del mensaje de alerta o mantenimiento que se mostrará en el encabezado del sitio.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* LIVE PREVIEW BOX */}
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase text-slate-400 tracking-wider flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5 text-blue-600" /> Vista Previa en Tiempo Real
              </Label>
              <div
                className={cn(
                  "p-3 rounded-2xl border shadow-sm transition-all duration-300 flex items-center justify-between gap-3 text-xs md:text-sm font-semibold",
                  currentBanner.banner_type === "maintenance" && "bg-gradient-to-r from-[#D4AF37] via-[#F3E5AB] to-[#D4AF37] text-slate-900 border-amber-300",
                  currentBanner.banner_type === "warning" && "bg-gradient-to-r from-rose-600 to-amber-600 text-white border-rose-500",
                  currentBanner.banner_type === "info" && "bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-500",
                  currentBanner.banner_type === "announcement" && "bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-emerald-500"
                )}
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  {currentBanner.banner_type === "maintenance" && <Sparkles className="h-4 w-4 shrink-0 text-slate-900" />}
                  {currentBanner.banner_type === "warning" && <AlertTriangle className="h-4 w-4 shrink-0 text-white" />}
                  {currentBanner.banner_type === "info" && <Info className="h-4 w-4 shrink-0 text-white" />}
                  {currentBanner.banner_type === "announcement" && <Megaphone className="h-4 w-4 shrink-0 text-white" />}

                  <div className="truncate">
                    <span className="font-extrabold uppercase tracking-wider mr-1.5">{currentBanner.title}:</span>
                    <span>{currentBanner.message || "Ingresa el texto del mensaje aquí..."}</span>
                  </div>
                </div>

                {currentBanner.action_text && (
                  <button className="px-3 py-1 bg-white/20 hover:bg-white/30 backdrop-blur-xs rounded-xl text-xs font-bold shrink-0 transition-all">
                    {currentBanner.action_text}
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Tipo de Banner</Label>
                <Select
                  value={currentBanner.banner_type || "maintenance"}
                  onValueChange={(val: any) => setCurrentBanner((prev) => ({ ...prev, banner_type: val }))}
                >
                  <SelectTrigger className="h-9 text-xs rounded-xl bg-slate-50">
                    <SelectValue placeholder="Selecciona tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maintenance">🛠️ Mantenimiento (Dorado)</SelectItem>
                    <SelectItem value="warning">⚠️ Alerta / Advertencia (Rojo)</SelectItem>
                    <SelectItem value="info">ℹ️ Información (Azul)</SelectItem>
                    <SelectItem value="announcement">📢 Anuncio / Noticia (Verde)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Título Corto</Label>
                <Input
                  value={currentBanner.title || ""}
                  onChange={(e) => setCurrentBanner((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Ej: Mantenimiento Programado"
                  className="h-9 text-xs rounded-xl bg-slate-50"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-700">Mensaje del Banner *</Label>
              <Textarea
                rows={3}
                value={currentBanner.message || ""}
                onChange={(e) => setCurrentBanner((prev) => ({ ...prev, message: e.target.value }))}
                placeholder="Escribe el mensaje claro y conciso que verán los usuarios..."
                className="text-xs rounded-xl bg-slate-50 resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 pt-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">Texto del Botón (Opcional)</Label>
                <Input
                  value={currentBanner.action_text || ""}
                  onChange={(e) => setCurrentBanner((prev) => ({ ...prev, action_text: e.target.value }))}
                  placeholder="Ej: Saber más / Ver estado"
                  className="h-9 text-xs rounded-xl bg-slate-50"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-700">URL del Botón (Opcional)</Label>
                <Input
                  value={currentBanner.action_url || ""}
                  onChange={(e) => setCurrentBanner((prev) => ({ ...prev, action_url: e.target.value }))}
                  placeholder="Ej: https://status.ejemplo.com"
                  className="h-9 text-xs rounded-xl bg-slate-50"
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-200/80">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold text-slate-800">Activo Inmediatamente</Label>
                <p className="text-[11px] text-slate-500">Muestra el banner de inmediato a todos los usuarios del sitio.</p>
              </div>
              <Switch
                checked={currentBanner.is_active ?? true}
                onCheckedChange={(val) => setCurrentBanner((prev) => ({ ...prev, is_active: val }))}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-200/80">
              <div className="space-y-0.5">
                <Label className="text-xs font-bold text-slate-800">Permitir Cerrar (Cerrable)</Label>
                <p className="text-[11px] text-slate-500">Permite a los usuarios ocultar el banner temporalmente.</p>
              </div>
              <Switch
                checked={currentBanner.is_dismissible ?? true}
                onCheckedChange={(val) => setCurrentBanner((prev) => ({ ...prev, is_dismissible: val }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-xs font-bold uppercase">
              Cancelar
            </Button>
            <Button
              onClick={handleSaveBanner}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase tracking-wider rounded-xl h-9 px-5 shadow-sm"
            >
              {saving ? "Guardando..." : "Guardar Banner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-rose-600">¿Eliminar este banner de mantenimiento?</DialogTitle>
            <DialogDescription className="text-xs text-slate-500 mt-1">
              Esta acción no se puede deshacer. El banner "{deleteTarget?.title}" será removido permanentemente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} className="text-xs font-bold">
              Cancelar
            </Button>
            <Button onClick={handleDeleteBanner} className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase rounded-xl">
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
